import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { View, CreateViewRequest, UpdateViewRequest } from '@/models/view';

export const useViews = (module: 'leads' | 'customers') => {
    const queryClient = useQueryClient();

    const {
        data: views,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['views', module],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<View[]>>(API_ENDPOINTS.CRM.VIEWS, {
                params: { module },
            });
            return data.data;
        },
    });

    const reorderViews = useMutation({
        mutationFn: async (viewIds: string[]) => {
            const { data } = await httpClient.post<ApiResponse<unknown>>(
                API_ENDPOINTS.CRM.VIEWS_REORDER,
                { module, view_ids: viewIds }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['views', module] });
        },
    });

    const createView = useMutation({
        mutationFn: async (viewData: CreateViewRequest) => {
            const { data: response } = await httpClient.post<ApiResponse<View>>(
                API_ENDPOINTS.CRM.VIEWS,
                viewData
            );
            return response.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['views', variables.module] });
        },
    });

    const updateView = useMutation({
        mutationFn: async ({ id, ...data }: UpdateViewRequest & { id: string }) => {
            const { data: response } = await httpClient.put<ApiResponse<View>>(
                API_ENDPOINTS.CRM.VIEW(id),
                data
            );
            return response.data;
        },
        onSuccess: updatedView => {
            queryClient.invalidateQueries({ queryKey: ['views', updatedView.module] });
            queryClient.invalidateQueries({ queryKey: ['views', updatedView.id] });
        },
    });

    const deleteView = useMutation({
        mutationFn: async (id: string) => {
            await httpClient.delete(API_ENDPOINTS.CRM.VIEW(id));
        },
        onSuccess: (_, id) => {
            // We need to invalidate all views queries since we don't know the module
            queryClient.invalidateQueries({ queryKey: ['views'] });
        },
    });

    const getView = (id: string) => {
        return useQuery({
            queryKey: ['views', id],
            queryFn: async () => {
                const { data } = await httpClient.get<ApiResponse<View>>(
                    API_ENDPOINTS.CRM.VIEW(id)
                );
                return data.data;
            },
            enabled: !!id,
        });
    };

    return {
        views: views || [],
        isLoading,
        error,
        createView,
        updateView,
        deleteView,
        reorderViews,
        getView,
    };
};
